// src/context/ChatContext/chatContext.tsx
import { createContext, useState, useEffect, useCallback, ReactNode, Dispatch, SetStateAction, useRef } from 'react';
import React from 'react';
import { ChatsType, MessageType } from '../../types/chat/chat';
import { chatService } from '../../services/chatService';
import { onlineStatusService } from '../../services/onlineStatusService';
import { getFirebaseChatUserId } from '../../utils/authHelper';
import { useAuth } from '../auth/AuthContext';

export interface ChatContextProps {
  chatData: ChatsType[];
  chatContent: any[];
  chatSearch: string;
  selectedChat: ChatsType | null;
  loading: boolean;
  error: string;
  activeChatId: string | null;
  currentUserId: string | null;
  messagesLoading: { [chatId: string]: boolean };
  isUserOnline: boolean;
  unreadCounts: { [chatId: string]: number };
  isChatDataLoaded: boolean;
  setChatContent: Dispatch<SetStateAction<any[]>>;
  setChatSearch: Dispatch<SetStateAction<string>>;
  setSelectedChat: Dispatch<SetStateAction<ChatsType | null>>;
  setActiveChatId: Dispatch<SetStateAction<string | null>>;
  sendMessage: (chatId: string, message: string) => Promise<void>;
  sendImageMessage: (chatId: string, imageFile: File) => Promise<void>;
  sendMessageWithAttachments: (chatId: string, message: string, files: File[]) => Promise<void>;
  markMessagesAsRead: (chatId: string) => Promise<void>;
  getUnreadCount: (chatId: string) => number;
  setLoading: Dispatch<SetStateAction<boolean>>;
  setError: Dispatch<SetStateAction<string>>;
  selectChatByUserId: (numericUserId: string) => void;
}

export const ChatContext = createContext<ChatContextProps>({
  chatData: [],
  chatContent: [],
  chatSearch: '',
  selectedChat: null,
  loading: true,
  error: '',
  activeChatId: null,
  currentUserId: null,
  messagesLoading: {},
  isUserOnline: false,
  unreadCounts: {},
  isChatDataLoaded: false,
  setChatContent: () => {},
  setChatSearch: () => {},
  setSelectedChat: () => {},
  setActiveChatId: () => {},
  sendMessage: async () => {},
  sendImageMessage: async () => {},
  sendMessageWithAttachments: async () => {},
  markMessagesAsRead: async () => {},
  getUnreadCount: () => 0,
  setLoading: () => {},
  setError: () => {},
  selectChatByUserId: () => {},
} as ChatContextProps);

const isValidPrefixedUserId = (id: string | null): boolean => {
  if (!id) return false;
  return (
    id.startsWith('nutritionistid_') ||
    id.startsWith('adminid_') ||
    id.startsWith('userid_')
  );
};

export const ChatProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();

  const [chatData, setChatData] = useState<ChatsType[]>([]);
  const [chatContent, setChatContent] = useState<any[]>([]);
  const [chatSearch, setChatSearch] = useState('');
  const [selectedChat, setSelectedChat] = useState<ChatsType | null>(null);
  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [messagesLoading, setMessagesLoading] = useState<{ [k: string]: boolean }>({});
  const [isUserOnline, setIsUserOnline] = useState(false);
  const [unreadCounts, setUnreadCounts] = useState<{ [k: string]: number }>({});
  const [isChatDataLoaded, setIsChatDataLoaded] = useState(false);

  const messageUnsubscribes = useRef<{ [k: string]: () => void }>({});
  const hasSetOnlineStatus = useRef(false);
  const chatUnsubscribeRef = useRef<(() => void) | null>(null);
  const messagesCache = useRef<{ [k: string]: MessageType[] }>({});

  // ── Unread helpers ─────────────────────────────────────────────────────────

  const calculateUnreadCount = (messages: MessageType[], uid: string): number =>
    messages.filter((m) => m.senderId !== uid && !(m.seenBy || []).includes(uid)).length;

  const getUnreadCount = (chatId: string) => unreadCounts[chatId] || 0;

  // ── Resolve userId when auth changes ──────────────────────────────────────
  //
  // NOTE: We read from localStorage here because AuthContext calls
  // initializeFirebaseChatUser(user) which stores the prefixed ID.
  // By the time this useEffect runs, the value should already be set.

  useEffect(() => {
    if (isAuthenticated && user) {
      // Small delay to ensure AuthContext has finished storing the ID
      const resolve = () => {
        const id = getFirebaseChatUserId();

        console.log('🔑 Resolving firebaseChatUserId:', id);

        if (!id) {
          console.warn('⚠️ firebaseChatUserId not found. Will retry in 500ms...');
          // Retry once — sometimes localStorage write and read race on first login
          setTimeout(() => {
            const retryId = getFirebaseChatUserId();
            if (retryId && isValidPrefixedUserId(retryId)) {
              console.log('✅ Retry succeeded:', retryId);
              setCurrentUserId(retryId);
            } else {
              console.error('❌ Retry failed. User must re-login.');
              setError('Session error. Please re-login.');
              setLoading(false);
            }
          }, 500);
          return;
        }

        if (!isValidPrefixedUserId(id)) {
          console.error(
            '❌ firebaseChatUserId has no prefix! Got:', id,
            '\nExpected: "nutritionistid_8"',
            '\nThis is stored by setFirebaseChatUser() in authHelper.ts.',
            '\nCheck that initializeFirebaseChatUser(user) is called in AuthContext after login.'
          );
          setError('Chat session error. Please re-login.');
          setLoading(false);
          return;
        }

        console.log('✅ currentUserId set to:', id);
        setCurrentUserId(id);
      };

      resolve();
    } else {
      // Logged out — clear everything
      setCurrentUserId(null);
      setChatData([]);
      setSelectedChat(null);
      setActiveChatId(null);
      setLoading(false);
      setIsUserOnline(false);
      setUnreadCounts({});
      setIsChatDataLoaded(false);
      messagesCache.current = {};

      chatUnsubscribeRef.current?.();
      chatUnsubscribeRef.current = null;

      Object.values(messageUnsubscribes.current).forEach((u) => u());
      messageUnsubscribes.current = {};
    }
  }, [isAuthenticated, user]);

  // ── Online status ──────────────────────────────────────────────────────────

  useEffect(() => {
    if (currentUserId && !hasSetOnlineStatus.current) {
      onlineStatusService.setUserOnline(currentUserId);
      setIsUserOnline(true);
      hasSetOnlineStatus.current = true;
    }

    return () => {
      if (currentUserId && hasSetOnlineStatus.current) {
        onlineStatusService.setUserOffline(currentUserId);
        onlineStatusService.cleanup();
        hasSetOnlineStatus.current = false;
        setIsUserOnline(false);
      }
    };
  }, [currentUserId]);

  // ── Subscribe to chats ────────────────────────────────────────────────────

  useEffect(() => {
    if (!currentUserId) {
      setLoading(false);
      return;
    }

    if (!isValidPrefixedUserId(currentUserId)) {
      console.error('❌ Aborting chat subscription — invalid userId:', currentUserId);
      setError('Invalid user session. Please re-login.');
      setLoading(false);
      return;
    }

    if (isChatDataLoaded) return;

    console.log('📡 Starting Firestore chat subscription for:', currentUserId);
    setLoading(true);

    const unsub = chatService.subscribeToChats(currentUserId, (chats) => {
      console.log(`📋 Received ${chats.length} chat(s) for: ${currentUserId}`);

      // Merge cached messages to prevent data loss on re-render
      const merged = chats.map((chat) => {
        const id = chat.id as string;
        const cached = messagesCache.current[id];
        if (!cached) return chat;

        const last = cached[cached.length - 1];
        const cachedAt = last?.createdAt ? new Date(last.createdAt) : null;

        return {
          ...chat,
          messages: cached,
          lastMessageAt:
            cachedAt && chat.lastMessageAt
              ? cachedAt > chat.lastMessageAt ? cachedAt : chat.lastMessageAt
              : cachedAt || chat.lastMessageAt,
        };
      });

      setChatData(merged);
      setLoading(false);
      setIsChatDataLoaded(true);
    });

    chatUnsubscribeRef.current = unsub;

    return () => {
      chatUnsubscribeRef.current?.();
      chatUnsubscribeRef.current = null;
      Object.values(messageUnsubscribes.current).forEach((u) => u());
      messageUnsubscribes.current = {};
    };
  }, [currentUserId, isChatDataLoaded]);

  // ── Preload messages ───────────────────────────────────────────────────────

  const preloadMessages = (chatId: string) => {
    if (messageUnsubscribes.current[chatId]) return;

    setMessagesLoading((p) => ({ ...p, [chatId]: true }));

    const unsub = chatService.subscribeToMessages(chatId, (messages) => {
      messagesCache.current[chatId] = messages;

      const last = messages[messages.length - 1];
      const newAt = last?.createdAt ? new Date(last.createdAt) : null;

      setChatData((prev) =>
        prev.map((c) =>
          c.id === chatId
            ? { ...c, messages, ...(newAt ? { lastMessageAt: newAt } : {}) }
            : c
        )
      );

      setSelectedChat((prev) =>
        prev?.id === chatId
          ? { ...prev, messages, ...(newAt ? { lastMessageAt: newAt } : {}) }
          : prev
      );

      setMessagesLoading((p) => ({ ...p, [chatId]: false }));
    });

    messageUnsubscribes.current[chatId] = unsub;
  };

  // ── Unread counts ──────────────────────────────────────────────────────────

  useEffect(() => {
    if (!currentUserId) return;

    const counts: { [k: string]: number } = {};
    chatData.forEach((chat) => {
      const id = chat.id as string;
      const msgs = messagesCache.current[id] || chat.messages || [];
      counts[id] = calculateUnreadCount(msgs, currentUserId);
    });

    setUnreadCounts(counts);
    const total = Object.values(counts).reduce((a, b) => a + b, 0);
    if (total > 0) console.log('📬 Unread total:', total);
  }, [chatData, currentUserId]);

  // ── Switch active chat ─────────────────────────────────────────────────────

  useEffect(() => {
    if (!activeChatId || !chatData.length) return;

    const chat = chatData.find((c) => c.id === activeChatId);
    if (!chat) return;

    const cached = messagesCache.current[activeChatId];
    setSelectedChat(cached ? { ...chat, messages: cached } : chat);

    if (!messageUnsubscribes.current[activeChatId]) preloadMessages(activeChatId);
  }, [activeChatId, chatData]);

  // ── Tab visibility ─────────────────────────────────────────────────────────

  useEffect(() => {
    const onVisibility = () => {
      if (!document.hidden && selectedChat?.id) {
        const cached = messagesCache.current[selectedChat.id as string];
        if (cached?.length) setSelectedChat((p) => p ? { ...p, messages: cached } : null);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [selectedChat]);

  // ── Send actions ───────────────────────────────────────────────────────────

  const sendMessage = async (chatId: string, message: string) => {
    if (!currentUserId) { setError('Please login'); return; }
    try { await chatService.sendMessage(chatId, message, currentUserId); }
    catch { setError('Failed to send message'); }
  };

  const sendImageMessage = async (chatId: string, file: File) => {
    if (!currentUserId) { setError('Please login'); return; }
    try { await chatService.sendImageMessage(chatId, file, currentUserId); }
    catch { setError('Failed to send image'); }
  };

  const sendMessageWithAttachments = async (chatId: string, message: string, files: File[]) => {
    if (!currentUserId) { setError('Please login'); return; }
    try { await chatService.sendMessageWithAttachments(chatId, message, files, currentUserId); }
    catch { setError('Failed to send attachments'); }
  };

  const markMessagesAsRead = async (chatId: string) => {
    if (!currentUserId) return;
    try {
      await chatService.markMessagesAsRead(chatId, currentUserId);
      setUnreadCounts((p) => ({ ...p, [chatId]: 0 }));
    } catch {
      console.error('❌ markMessagesAsRead failed');
    }
  };

  // Find a chat by the client's numeric backend user ID (e.g. "123" → looks for "userid_123")
  const selectChatByUserId = useCallback((numericUserId: string) => {
    const target = `userid_${numericUserId}`;
    const chat = chatData.find((c) => c.clientUserId === target);
    if (chat) {
      const cached = messagesCache.current[chat.id as string];
      setSelectedChat(cached ? { ...chat, messages: cached } : chat);
      setActiveChatId(chat.id as string);
    }
  }, [chatData]);

  const value: ChatContextProps = {
    chatData, chatContent, chatSearch, selectedChat, loading, error,
    activeChatId, currentUserId, messagesLoading, isUserOnline, unreadCounts,
    isChatDataLoaded, setChatContent, setChatSearch, setSelectedChat,
    setActiveChatId, sendMessage, sendImageMessage, sendMessageWithAttachments,
    markMessagesAsRead, getUnreadCount, setError, setLoading, selectChatByUserId,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};